import { useEffect, useState } from "react";
import Styles from "../../../styles/clientDashboard.module.css";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

//Format numbers in the Indian numbering system
function formatNumber(value) {
  if (value >= 10000000) {
    return `${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `${(value / 100000).toFixed(2)} L`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(2)} K`;
  } else {
    return `${value}`;
  }
}

const LineChart = ({ spendAllocation }) => {
  const statsData = spendAllocation.reduce((acc, item) => {
    const { productType, totalPrice } = item;
    let existingProduct = acc.find((p) => p.productType === productType);

    if (!existingProduct) {
      existingProduct = { productType, totalPrice: 0 };
      acc.push(existingProduct);
    }
    existingProduct.totalPrice += totalPrice;
    return acc;
  }, []);

  console.log(statsData);

  const [selectedStatIndex, setSelectedStatIndex] = useState(0);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (Array.isArray(spendAllocation) && spendAllocation.length > 0) {
      const selectedProductType = statsData[selectedStatIndex]?.productType;

      // ✅ Filter only the selected product type's data
      const filteredData = spendAllocation.filter(
        (item) => item.productType === selectedProductType
      );

      setChartData(filteredData);
    }
  }, [selectedStatIndex, spendAllocation]);

  const maxTotalPrice = Math.max(
    ...spendAllocation.map((item) => item.totalPrice),
    0
  );
  const dynamicMax = Math.ceil(maxTotalPrice / 10000) * 10000; // Round to nearest 10,000

  // Adjust Y-axis scale dynamically
  let scaleFactor = 1;
  let suffix = "";

  if (dynamicMax >= 10000000) {
    scaleFactor = 10000000;
    suffix = "Cr";
  } else if (dynamicMax >= 100000) {
    scaleFactor = 100000;
    suffix = "L";
  } else if (dynamicMax >= 1000) {
    scaleFactor = 1000;
    suffix = "K";
  }

  // Generate dynamic Y-axis ticks
  const yAxisTicks = Array.from(
    { length: Math.ceil(dynamicMax / scaleFactor) + 1 },
    (_, i) => i * scaleFactor
  );

  const handleStatClick = (index) => {
    setSelectedStatIndex(index);
  };

  const monthOrder = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const defaultChartData = [
    { month: "Jan", totalPrice: 0 },
    { month: "Feb", totalPrice: 0 },
    { month: "Mar", totalPrice: 0 },
    { month: "Apr", totalPrice: 0 },
    { month: "May", totalPrice: 0 },
  ];

  // Sort chart data based on the month order
  const sortedChartData = [...chartData].sort(
    (a, b) => monthOrder.indexOf(a.month) - monthOrder.indexOf(b.month)
  );

  return (
    <div className="flex h-full w-full justify-center items-center">
      <div className="h-[450px] w-full rounded-md shadow-lg bg-white m-auto  p-6">
        <div className="text-black text-xl font-bold mb-4">
          <h2 className="text-lg font-semibold">
            Spend Allocation Of Products
          </h2>
        </div>

        <div
          className={`w-full overflow-x-auto mb-6 ${Styles["custom1-scrollbar"]}`}
        >
          <div className="flex space-x-4 flex-nowrap mb-0.5">
            {statsData.map((stat, index) => (
              <div
                key={index}
                className={`flex flex-col min-w-[150px] p-2 cursor-pointer rounded-sm ${selectedStatIndex === index
                    ? "bg-gradient-to-t from-blue-50 to-blue-0 border-b-4 border-blue-500"
                    : "bg-white"
                  }`}
                onClick={() => handleStatClick(index)}
              >
                <p className="text-xl text-black font-bold">
                  {formatNumber(stat?.totalPrice)}
                </p>
                <p className="text-gray-500 text-sm">{stat?.productType}</p>
              </div>
            ))}
          </div>
        </div>

        {/* LineChart */}
        <ResponsiveContainer width="100%" height={250}>
          <RechartsLineChart
            data={
              sortedChartData.length > 0 ? sortedChartData : defaultChartData
            }
            syncId="anyId"
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid stroke="none" />
            <XAxis
              dataKey="month"
              label={{
                value: "Months",
                position: "bottom",
                fontSize: 14,
                fontWeight: "bold",
              }}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              label={{
                angle: -90,
                position: "insideLeft",
                fontSize: 14,
                fontWeight: "bold",
              }}
              domain={[0, dynamicMax]}
              ticks={yAxisTicks}
              tickFormatter={(value) => `${value / scaleFactor}${suffix}`}
            />
            <Tooltip formatter={(value) => formatNumber(value)} />
            <Line
              type="monotone"
              dataKey="totalPrice"
              stroke="#00aaff"
              fill="#00aaff"
              name="Total Price"
              strokeWidth={4}
            />
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LineChart;
